import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGeolocation } from '../../../hooks/useGeolocation';
import { useNearbyAdoptions } from '../../../hooks/useNearbyAdoptions';
import { adoptableCats as mockCats } from '../../../mocks/adoptableCats';

const filters = {
  age: ['All Ages', 'Baby', 'Young', 'Adult', 'Senior'],
  gender: ['Any', 'Male', 'Female'],
};

const sortOptions = ['Nearest', 'Newest', 'Age: Young to Old', 'Age: Old to Young'];
const radiusOptions = [5, 10, 25, 50, 100];

interface Props {
  onInquiry: (catName: string, shelterName: string) => void;
}

// Map mock data to match API format
function mapMockCatsToAPI(radius: number) {
  return mockCats
    .filter((cat) => {
      const dist = parseFloat(String(cat.distance));
      return dist <= radius;
    })
    .map((cat) => {
      const [cityRaw, stateRaw] = (cat.shelterCity || '').split(', ');
      const ageMap: Record<string, string> = {
        '3 months': 'Baby',
        '8 months': 'Baby',
        '1 year': 'Young',
        '1.5 years': 'Young',
        '2 years': 'Young',
        '4 years': 'Adult',
        '5 years': 'Adult',
        '6 years': 'Adult',
        '9 years': 'Senior',
      };
      return {
        id: String(cat.id),
        name: cat.name,
        breed: cat.breed,
        age: ageMap[cat.age] || 'Adult',
        gender: cat.gender,
        size: 'Medium',
        description: cat.personality,
        image: cat.image,
        shelterId: String(cat.id),
        shelterName: cat.shelter,
        distance: parseFloat(String(cat.distance)),
        photos: [cat.image],
        attributes: {
          spayedNeutered: cat.neutered,
          houseTrained: true,
          declawed: false,
          specialNeeds: false,
        },
        environment: {
          children: cat.personality.toLowerCase().includes('kid') || cat.personality.toLowerCase().includes('child'),
          dogs: cat.personality.toLowerCase().includes('dog'),
          cats: true,
        },
        contact: {
          email: '',
          phone: '',
          address: {
            city: cityRaw || 'Portland',
            state: stateRaw || 'OR',
            postcode: '',
            country: 'US',
          },
        },
      };
    });
}

export default function AdoptableCatsGrid({ onInquiry }: Props) {
  const { latitude, longitude } = useGeolocation();
  const [radius, setRadius] = useState(25);
  const { cats: apiCats, isLoading, error, lastUpdated, refetch } = useNearbyAdoptions(latitude, longitude, radius);

  // Fallback to mock data if API returns empty or errors
  const cats = useMemo(() => {
    if (apiCats && apiCats.length > 0) {
      return apiCats;
    }
    return mapMockCatsToAPI(radius);
  }, [apiCats, radius]);

  const [selectedAge, setSelectedAge] = useState('All Ages');
  const [selectedGender, setSelectedGender] = useState('Any');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('Nearest');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Filter and sort cats
  const filteredAndSortedCats = useMemo(() => {
    let result = cats.filter((cat) => {
      const matchesAge = selectedAge === 'All Ages' || cat.age === selectedAge;
      const matchesGender = selectedGender === 'Any' || cat.gender === selectedGender;
      const matchesSearch =
        searchQuery === '' ||
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.breed.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.shelterName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.contact.address.city.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesAge && matchesGender && matchesSearch;
    });

    // Sort
    if (sortBy === 'Nearest') {
      result.sort((a, b) => a.distance - b.distance);
    } else if (sortBy === 'Newest') {
      result.reverse(); // API returns newest first typically
    } else if (sortBy === 'Age: Young to Old') {
      const ageOrder: Record<string, number> = { Baby: 0, Young: 1, Adult: 2, Senior: 3 };
      result.sort((a, b) => (ageOrder[a.age] || 2) - (ageOrder[b.age] || 2));
    } else if (sortBy === 'Age: Old to Young') {
      const ageOrder: Record<string, number> = { Baby: 0, Young: 1, Adult: 2, Senior: 3 };
      result.sort((a, b) => (ageOrder[b.age] || 2) - (ageOrder[a.age] || 2));
    }

    return result;
  }, [cats, selectedAge, selectedGender, searchQuery, sortBy]);

  const minutesAgo = lastUpdated ? Math.floor((Date.now() - lastUpdated.getTime()) / 60000) : null;

  return (
    <section className="py-12 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4">
            <span className="text-accent">Cats</span> Looking for Homes
          </h2>
          <p className="font-body text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">
            Every cat deserves a loving home. Browse adoptable cats from local shelters near you.
          </p>
        </div>

        {/* Controls Bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 bg-gray-50 rounded-xl p-4">
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <label className="font-body text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-body font-medium text-gray-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Distance Radius Slider */}
          <div className="flex items-center gap-3 flex-1 max-w-xs">
            <label className="font-body text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap">Radius:</label>
            <input
              type="range"
              min="0"
              max="4"
              step="1"
              value={radiusOptions.indexOf(radius)}
              onChange={(e) => setRadius(radiusOptions[parseInt(e.target.value)])}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <span className="font-body text-sm font-semibold text-accent whitespace-nowrap">{radius} mi</span>
          </div>

          {/* Last Updated & Refresh */}
          <div className="flex items-center gap-3">
            {minutesAgo !== null && (
              <span className="font-body text-xs text-gray-400">
                Updated {minutesAgo === 0 ? 'just now' : `${minutesAgo}m ago`}
              </span>
            )}
            <button
              onClick={refetch}
              disabled={isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-accent hover:bg-accent/5 text-gray-600 hover:text-accent transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh data"
            >
              <i className={`ri-refresh-line text-lg ${isLoading ? 'animate-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 sm:mb-10 space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Search by name, breed, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
            />
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <span className="font-body text-xs text-gray-400 uppercase tracking-wider">Age:</span>
              {filters.age.map((age) => (
                <button
                  key={age}
                  onClick={() => setSelectedAge(age)}
                  className={`px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedAge === age
                      ? 'bg-accent text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {age}
                </button>
              ))}
            </div>
            <div className="w-px h-6 bg-gray-200 hidden sm:block self-center"></div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <span className="font-body text-xs text-gray-400 uppercase tracking-wider">Gender:</span>
              {filters.gender.map((gender) => (
                <button
                  key={gender}
                  onClick={() => setSelectedGender(gender)}
                  className={`px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedGender === gender
                      ? 'bg-accent text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {gender}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="font-body text-sm text-gray-400 mb-6">
          Showing <strong className="text-gray-700">{filteredAndSortedCats.length}</strong> adoptable cats
        </p>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
                <div className="w-full h-56 sm:h-64 bg-gray-200"></div>
                <div className="p-4 sm:p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State - Show fallback data instead */}
        {error && !isLoading && cats.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <i className="ri-information-line text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="font-body text-sm text-amber-800">
                <strong>Using local data:</strong> Live shelter data is temporarily unavailable. Showing sample adoptable cats from our partner shelters.
              </p>
            </div>
          </div>
        )}

        {/* Filtered Empty State */}
        {!isLoading && filteredAndSortedCats.length === 0 && cats.length > 0 && (
          <div className="text-center py-16">
            <i className="ri-emotion-sad-line text-5xl text-gray-300 mb-4 block"></i>
            <p className="font-heading font-bold text-lg text-gray-400">No cats match your filters</p>
            <p className="font-body text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Cat Cards Grid */}
        {!isLoading && filteredAndSortedCats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filteredAndSortedCats.map((cat, index) => {
              const tagColor = cat.attributes.specialNeeds
                ? 'bg-purple-100 text-purple-700'
                : cat.age === 'Baby'
                ? 'bg-pink-100 text-pink-700'
                : cat.age === 'Senior'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-emerald-100 text-emerald-700';

              const tagText = cat.attributes.specialNeeds
                ? 'Special Needs'
                : cat.age === 'Baby'
                ? 'Kitten'
                : cat.age === 'Senior'
                ? 'Senior'
                : 'Available';

              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  {/* Image */}
                  <Link to={`/adopt/cat/${cat.id}`} className="block relative w-full h-56 sm:h-64 overflow-hidden cursor-pointer">
                    <img
                      src={cat.image}
                      alt={`${cat.name} - ${cat.breed} available for adoption`}
                      title={`${cat.name} - adoptable ${cat.breed}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://public.readdy.ai/ai/img_res/12ccbc3c302dbb1af0de918c7a5ad1d4.jpg';
                      }}
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-body font-semibold ${tagColor}`}>
                        {tagText}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                      <i className="ri-map-pin-line text-accent text-xs"></i>
                      <span className="text-xs font-body font-medium text-gray-700">{cat.distance} mi</span>
                    </div>
                  </Link>

                  {/* Info */}
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between mb-2">
                      <Link to={`/adopt/cat/${cat.id}`} className="cursor-pointer hover:text-accent transition-colors">
                        <h3 className="font-heading font-bold text-lg text-gray-900">{cat.name}</h3>
                        <p className="font-body text-xs text-gray-500">{cat.breed}</p>
                      </Link>
                      <button
                        onClick={() => setExpandedCard(expandedCard === cat.id ? null : cat.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-accent/10 text-gray-500 hover:text-accent transition-all cursor-pointer"
                        aria-label="More info"
                      >
                        <i className={`ri-arrow-${expandedCard === cat.id ? 'up' : 'down'}-s-line text-lg`}></i>
                      </button>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center gap-1 text-xs font-body text-gray-500">
                        <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        {cat.age}
                      </span>
                      <span className="flex items-center gap-1 text-xs font-body text-gray-500">
                        <i className={`ri-${cat.gender === 'Male' ? 'men' : 'women'}-line text-sm w-4 h-4 flex items-center justify-center`}></i>
                        {cat.gender}
                      </span>
                      <span className="flex items-center gap-1 text-xs font-body text-gray-500">
                        <i className="ri-home-heart-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        {cat.size}
                      </span>
                    </div>

                    <p className="font-body text-sm text-gray-600 leading-relaxed mb-3 line-clamp-2">
                      {cat.description}
                    </p>

                    {/* Expanded Details */}
                    {expandedCard === cat.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-3 pt-3 border-t border-gray-100"
                      >
                        <div className="flex flex-wrap gap-2 mb-3">
                          {cat.attributes.spayedNeutered && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 rounded-md text-xs font-body">
                              <i className="ri-check-line text-xs"></i> Spayed/Neutered
                            </span>
                          )}
                          {cat.attributes.houseTrained && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 rounded-md text-xs font-body">
                              <i className="ri-check-line text-xs"></i> House Trained
                            </span>
                          )}
                          {cat.environment.children && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-body">
                              <i className="ri-check-line text-xs"></i> Good with Kids
                            </span>
                          )}
                          {cat.environment.dogs && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-body">
                              <i className="ri-check-line text-xs"></i> Good with Dogs
                            </span>
                          )}
                        </div>
                        <p className="font-body text-xs text-gray-400">
                          <i className="ri-map-pin-2-line"></i> {cat.contact.address.city}, {cat.contact.address.state} &middot; {cat.shelterName}
                        </p>
                      </motion.div>
                    )}

                    {/* CTA */}
                    <div className="flex gap-2">
                      <Link
                        to={`/adopt/cat/${cat.id}`}
                        className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-heading font-semibold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                      >
                        <i className="ri-eye-line"></i>
                        View Profile
                      </Link>
                      <button
                        onClick={() => onInquiry(cat.name, cat.shelterName)}
                        className="flex-1 py-2.5 bg-accent hover:bg-accent/90 text-white font-heading font-semibold text-sm rounded-lg transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                      >
                        <i className="ri-heart-line"></i>
                        Inquire
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}